import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

const PREPARE_ERROR = "Could not prepare this PDF in your browser. Reload the page and try again.";
const PROCESS_TIMEOUT = 20000;

export default class extends Controller {
  static targets = ["dialog", "filename", "password", "passwordSection", "choice", "error", "submit", "storedError", "storedSubmit"];
  static values = { workerUrl: String, sourceUrl: String, filename: String };

  connect () {
    this.discard();
  }

  disconnect () {
    this.discard();
  }

  discard () {
    this.request?.abort();
    clearTimeout(this.timeout);
    this.worker?.terminate();
    this.worker = null;
    this.passwordTarget.value = "";
    this.dialogTarget.open = false;
    this.queue = [];
    this.current = null;
    this.closing = false;
    this.resetChoice();
    if (this.hasStoredSubmitTarget) this.storedSubmitTarget.disabled = false;
  }

  async unlockStored (event) {
    event.preventDefault();
    if (this.storedSubmitTarget.disabled) return;

    this.storedSubmitTarget.disabled = true;
    this.storedErrorTarget.textContent = "";
    this.request = new AbortController();
    try {
      const response = await fetch(this.sourceUrlValue, { signal: this.request.signal, cache: "no-store" });
      if (!response.ok) throw new Error();
      const file = new File([await response.blob()], this.filenameValue, { type: "application/pdf" });
      this.queue.push({ file, complete: (output, error) => this.replaceStored(output, error) });
      this.processNext();
    } catch (error) {
      if (error.name !== "AbortError") this.storedErrorTarget.textContent = "Could not load this PDF. Please try again.";
      this.storedSubmitTarget.disabled = false;
    }
  }

  async replaceStored (output, error) {
    try {
      if (error) {
        if (error !== "Upload cancelled.") this.storedErrorTarget.textContent = error;
        return;
      }
      if (!output) throw new Error();

      const body = new FormData();
      body.append("file", output, this.filenameValue);
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
      const response = await fetch(this.sourceUrlValue, {
        method: "PUT",
        body,
        headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {},
        signal: this.request.signal,
      });
      const result = await response.json();
      if (response.ok) Turbo.visit(result.redirect_url);
      else this.storedErrorTarget.textContent = result.error;
    } catch (error) {
      if (error.name !== "AbortError") this.storedErrorTarget.textContent = "Could not save the unlocked PDF. Please try again.";
    } finally {
      this.storedSubmitTarget.disabled = false;
    }
  }

  prepare (event) {
    const { file } = event.detail;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) return;

    event.preventDefault();
    this.queue.push(event.detail);
    this.processNext();
  }

  processNext () {
    if (this.current || this.closing || !this.queue.length) return;

    this.current = this.queue.shift();
    this.resetChoice();
    try {
      this.worker = new Worker(this.workerUrlValue, { type: "module" });
      this.worker.onmessage = ({ data }) => this.receive(data);
      this.worker.onerror = (event) => {
        event.preventDefault();
        this.preparationFailed();
      };
      this.send({ command: "prepare", file: this.current.file });
    } catch {
      this.preparationFailed();
    }
  }

  send (message) {
    this.timeout = setTimeout(() => this.preparationFailed(), PROCESS_TIMEOUT);
    this.worker.postMessage(message);
  }

  receive (data) {
    clearTimeout(this.timeout);
    if (data.status === "ready") {
      this.finish(data.output ? new Blob([data.output], { type: "application/pdf" }) : null);
    } else if (["password_required", "incorrect_password"].includes(data.status)) {
      this.filenameTarget.textContent = this.current.file.name;
      this.errorTarget.textContent = data.status === "incorrect_password" ? "That password didn't work. Please try again." : "";
      this.submitTarget.loading = false;
      this.passwordTarget.disabled = false;
      this.disableChoices(false);
      this.dialogTarget.show();
      this.focusPassword();
    } else {
      this.preparationFailed();
    }
  }

  preparationFailed () {
    this.finish(null, this.hasSourceUrlValue ? PREPARE_ERROR : undefined);
  }

  unlock (event) {
    event.preventDefault();
    if (!this.current || this.submitTarget.loading) return;

    const password = this.passwordTarget.value;
    this.passwordTarget.value = "";
    this.passwordTarget.disabled = true;
    this.submitTarget.loading = true;
    this.disableChoices(true);
    this.errorTarget.textContent = "";
    this.send({ command: "unlock", password });
  }

  cancel (event) {
    event.preventDefault();
    if (!this.current) return;

    this.current.file.cancelled = true;
    this.finish(null, "Upload cancelled.");
  }

  submitChoice (event) {
    event.preventDefault();
    if (!this.current || this.submitTarget.loading) return;

    if (this.hasChoiceTarget && this.choiceTarget.value === "locked") this.finish(null);
    else this.unlock(event);
  }

  choose (event) {
    if (event && event.target !== this.choiceTarget) return;

    const locked = this.choiceTarget.value === "locked";
    this.passwordTarget.value = "";
    this.passwordSectionTarget.hidden = locked;
    this.passwordTarget.disabled = locked;
    this.errorTarget.textContent = "";
    this.submitTarget.textContent = locked ? "Upload without unlocking" : "Unlock and upload";
    if (!locked && this.dialogTarget.open) this.focusPassword();
  }

  resetChoice () {
    if (!this.hasChoiceTarget) return;

    this.choiceTarget.value = "unlock";
    this.choose();
    this.disableChoices(false);
  }

  disableChoices (disabled) {
    if (this.hasChoiceTarget) this.choiceTarget.querySelectorAll("sl-radio").forEach(radio => { radio.disabled = disabled; });
  }

  keepPasswordKeys (event) {
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(event.key)) event.stopPropagation();
  }

  focusPassword () {
    if (!this.passwordTarget.disabled) this.passwordTarget.focus();
  }

  async finish (output, error) {
    if (!this.current) return;

    clearTimeout(this.timeout);
    this.worker?.terminate();
    this.worker = null;
    this.passwordTarget.value = "";
    this.submitTarget.loading = false;
    this.passwordTarget.disabled = false;
    this.disableChoices(false);
    const { complete } = this.current;
    this.current = null;
    this.closing = true;
    const closed = this.dialogTarget.hide();
    complete(output, error);
    await closed;
    this.closing = false;
    this.processNext();
  }
}
